'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { CSSProperties, FC, useEffect, useId, useRef } from 'react';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { gsap } from '@/lib/gsap';
import { ResultInsight } from '@/types/lead-magnet';
import css from './style.module.scss';
const ACCORDION_TRANSITION_MS = 500;
const SCROLL_OFFSET = 20;
interface Props {
  insight: ResultInsight;
  icon: string | null;
  isOpen: boolean;
  expandLabel: string;
  collapseLabel: string;
  recommendedActionLabel: string;
  shouldAutoScroll: boolean;
  onToggle: () => void;
}
const BreakdownItem: FC<Props> = t0 => {
  const $ = _c(56);
  const {
    insight,
    icon,
    isOpen,
    expandLabel,
    collapseLabel,
    recommendedActionLabel,
    shouldAutoScroll,
    onToggle
  } = t0;
  const device = useDevice();
  const cardRef = useRef(null);
  const id = useId();
  const panelId = `${id}-panel`;
  const activatorId = `${id}-activator`;
  const hasPanel = !!insight.description || !!insight.recommendedAction;
  const isGlowing = device === "desktop" && isOpen;
  let t1;
  let t2;
  if ($[0] !== isGlowing) {
    t1 = () => {
      const card = cardRef.current;
      if (!isGlowing || !card) {
        return;
      }
      gsap.set(card, {
        "--x": "50%",
        "--y": "50%"
      });
      const handleMouseMove = event => {
        const {
          left,
          top
        } = card.getBoundingClientRect();
        gsap.to(card, {
          "--x": `${event.clientX - left}px`,
          "--y": `${event.clientY - top}px`,
          duration: 0.2
        });
      };
      card.addEventListener("mousemove", handleMouseMove);
      return () => card.removeEventListener("mousemove", handleMouseMove);
    };
    t2 = [isGlowing];
    $[0] = isGlowing;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== device || $[4] !== isOpen || $[5] !== shouldAutoScroll) {
    t3 = () => {
      if (device !== "mobile" || !isOpen || !shouldAutoScroll) {
        return;
      }
      const card_0 = cardRef.current;
      if (!card_0) {
        return;
      }
      const timeout = window.setTimeout(() => {
        const topBar = document.querySelector("[data-lead-magnet-top-bar]");
        const offset = (topBar?.offsetHeight ?? 0) + SCROLL_OFFSET;
        window.scrollTo({
          top: window.scrollY + card_0.getBoundingClientRect().top - offset,
          behavior: "smooth"
        });
      }, ACCORDION_TRANSITION_MS);
      return () => window.clearTimeout(timeout);
    };
    t4 = [device, isOpen, shouldAutoScroll];
    $[3] = device;
    $[4] = isOpen;
    $[5] = shouldAutoScroll;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = isOpen && css.opened;
  let t6;
  if ($[8] !== t5) {
    t6 = clsx(css.signal, t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isGlowing) {
    t7 = isGlowing ? <div className={css.glowWrapper} /> : null;
    $[10] = isGlowing;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = !hasPanel;
  let t9;
  if ($[12] !== icon) {
    t9 = icon ? <span className={css.iconWrap}><span className={css.icon} style={{
        "--signal-icon": `url(${icon})`
      } as CSSProperties} /></span> : null;
    $[12] = icon;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== insight.title) {
    t10 = <Typography variant="h6" tag="h3" className={css.title}>{insight.title}</Typography>;
    $[14] = insight.title;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== insight.shortSummary) {
    t11 = insight.shortSummary ? <Typography variant="body-m" tag="span" className={css.summary}>{insight.shortSummary}</Typography> : null;
    $[16] = insight.shortSummary;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== collapseLabel || $[19] !== expandLabel || $[20] !== hasPanel || $[21] !== isOpen) {
    t12 = hasPanel ? <span className={css.toggle}><Typography uppercase={true} variant="body-s" tag="span" className={css.toggleLabel}>{isOpen ? collapseLabel : expandLabel}</Typography><svg className={css.chevron} width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 5L8 11L14 5" stroke="currentColor" strokeWidth="2" /></svg></span> : null;
    $[18] = collapseLabel;
    $[19] = expandLabel;
    $[20] = hasPanel;
    $[21] = isOpen;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== activatorId || $[24] !== isOpen || $[25] !== onToggle || $[26] !== panelId || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t8 || $[31] !== t9) {
    t13 = <button type="button" id={activatorId} className={css.activator} aria-expanded={isOpen} aria-controls={panelId} disabled={t8} onClick={onToggle}>{t9}{t10}{t11}{t12}</button>;
    $[23] = activatorId;
    $[24] = isOpen;
    $[25] = onToggle;
    $[26] = panelId;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t8;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = !isOpen;
  let t15;
  if ($[33] !== insight.shortSummary) {
    t15 = insight.shortSummary ? <Typography variant="body-l" tag="p" className={css.panelSummary}>{insight.shortSummary}</Typography> : null;
    $[33] = insight.shortSummary;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== insight.description) {
    t16 = insight.description ? <Typography variant="body-m" tag="div" className={css.description}><StrapiRichContent content={insight.description} /></Typography> : null;
    $[35] = insight.description;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== insight.recommendedAction || $[38] !== recommendedActionLabel) {
    t17 = insight.recommendedAction ? <div className={css.action}><Typography uppercase={true} variant="body-s" tag="p" className={css.actionLabel}>{recommendedActionLabel}</Typography><Typography variant="body-m" tag="div" className={css.actionBody}><StrapiRichContent content={insight.recommendedAction} /></Typography></div> : null;
    $[37] = insight.recommendedAction;
    $[38] = recommendedActionLabel;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17) {
    t18 = <div className={css.panelInner}>{t15}{t16}{t17}</div>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== activatorId || $[45] !== panelId || $[46] !== t14 || $[47] !== t18) {
    t19 = <div id={panelId} className={css.panel} role="region" aria-labelledby={activatorId} inert={t14}>{t18}</div>;
    $[44] = activatorId;
    $[45] = panelId;
    $[46] = t14;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t19) {
    t20 = <div className={css.card}>{t13}{t19}</div>;
    $[49] = t13;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t20 || $[53] !== t6 || $[54] !== t7) {
    t21 = <li ref={cardRef} className={t6}>{t7}{t20}</li>;
    $[52] = t20;
    $[53] = t6;
    $[54] = t7;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  return t21;
};
export default BreakdownItem;